import type { AdminPaymentSummary } from '@/types/api'
import { Badge, StatusBadge } from '@/components/ui/StatusBadge'
import { toneForStatus } from '@/components/ui/status-tone'
import { StackedCell } from '@/components/ui/RowActions'
import {
  awaitsProof,
  describePaymentMethod,
  describePointsCoverage,
  describeVerificationStatus,
  formatMoney,
  verificationTone,
} from './describe'
import styles from './PaymentCell.module.css'

/**
 * The payment column shared by the Trips and Trip Requests tables.
 *
 * One component rather than two nearly-identical blocks, so the two screens
 * cannot end up describing the same booking differently.
 *
 * What a row has to answer at a glance is "has this been paid, and does anyone
 * need to do something about it?". So the method and the payment status are
 * always shown; the verification badge appears only where a human is involved,
 * because a card payment carrying a "not required" badge is noise on every row.
 */
export function PaymentMethodCell({ payment }: { payment: AdminPaymentSummary }) {
  const coverage = describePointsCoverage(payment)

  return (
    <StackedCell
      primary={
        <span className={styles.methodLine}>
          <Badge tone={toneForStatus(payment.paymentMethodCode)} dot>
            {describePaymentMethod(payment)}
          </Badge>
          {payment.canVerify ? (
            <Badge tone={verificationTone(payment.verificationStatus)}>
              {awaitsProof(payment) ? 'Awaiting proof' : describeVerificationStatus(payment.verificationStatus)}
            </Badge>
          ) : null}
        </span>
      }
      secondary={coverage ?? undefined}
    />
  )
}

/**
 * The money column: what the booking is worth, and how it was split.
 *
 * The split lines are only rendered when points were actually applied. Showing
 * "Points EGP 0.00" on every historical row would bury the handful where it
 * matters.
 */
export function PaymentAmountCell({ payment }: { payment: AdminPaymentSummary }) {
  const hasPoints = payment.pointsUsedMinor > 0

  return (
    <div className={styles.amounts}>
      <span className={styles.total}>{formatMoney(payment.totalAmount, payment.currency)}</span>
      {hasPoints ? (
        <span className={styles.split}>
          <span className={styles.points}>−{formatMoney(payment.pointsUsed, payment.currency)} points</span>
          <span className={styles.external}>
            {payment.externalAmountMinor > 0
              ? `${formatMoney(payment.externalAmount, payment.currency)} ${describePaymentMethod(payment).toLowerCase()}`
              : 'Nothing to pay'}
          </span>
        </span>
      ) : null}
    </div>
  )
}

/** The booking's own payment status, unchanged from before this feature. */
export function PaymentStatusCell({ payment }: { payment: AdminPaymentSummary }) {
  return <StatusBadge status={payment.paymentStatus} />
}
